import { useEffect, useRef, useState } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { APP_SMART_LINK_URL } from '@/constants';

/** How long "Copied" stands before reverting. */
const COPIED_FEEDBACK_MS = 2_000;

const SHARE_TITLE = 'Mia — gift a child a brighter future';
const SHARE_TEXT =
  'I just gifted towards a child’s future with Mia. You can start a pot for your own kids, or gift to one.';

/**
 * Passing Mia on to a friend.
 */
function ShareMia() {
  const [open, setOpen] = useState(false);

  // Read once: it does not change under a running page, and reading it during
  // render keeps the button list stable.
  const [canWebShare] = useState(
    () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
  );

  return (
    <>
      <div className='flex items-center gap-3'>
        <span className='h-px flex-1 bg-[#e3e0d8]' />
        <span className='text-xs font-semibold text-[#8a938c]'>
          or spread the word
        </span>
        <span className='h-px flex-1 bg-[#e3e0d8]' />
      </div>

      <div>
        <button
          type='button'
          onClick={() => setOpen(true)}
          className='flex h-12 w-full items-center justify-center gap-[9px] rounded-[14px] bg-[#f3efe7] text-[15px] font-bold text-[#256956]'
        >
          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#256956' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM2 21a6 6 0 0 1 12 0M17 11a3 3 0 1 0 0-6M22 21a5 5 0 0 0-4-4.9' />
          </svg>
          Share Mia
        </button>
        <p className='mt-2 text-center text-xs leading-[1.4] text-[#8a938c]'>
          So they can gift investments to a child in their life
        </p>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className='text-center'>
          <SheetHeader className='w-full text-center'>
            <SheetTitle className='text-[20px] font-bold tracking-[-0.01em] text-[#1a201d]'>
              Share Mia
            </SheetTitle>
          </SheetHeader>

          <SheetDescription asChild>
            <p className='mt-[7px] text-[13px] leading-[1.5] text-[#5a635e]'>
              Send them a link so they can start a pot for their own kids, or
              gift to one, just like you did.
            </p>
          </SheetDescription>

          <LinkRow />

          <div className='mt-[13px] flex gap-[11px]'>
            <ShareOption
              as='link'
              href={`https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${APP_SMART_LINK_URL}`)}`}
              label='WhatsApp'
              icon={
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#25d366' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <path d='M21 11.5a8.4 8.4 0 0 1-12.3 7.5L3 21l1.9-5.7A8.4 8.4 0 1 1 21 11.5z' />
                </svg>
              }
            />
            <ShareOption
              as='link'
              href={`mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(`${SHARE_TEXT}\n\n${APP_SMART_LINK_URL}`)}`}
              label='Email'
              icon={
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#2a6fdb' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <rect x='2' y='4' width='20' height='16' rx='2' />
                  <path d='m22 7-10 6L2 7' />
                </svg>
              }
            />
            {canWebShare && (
              <ShareOption
                as='button'
                label='More'
                onClick={() => {
                  void navigator
                    .share({
                      title: SHARE_TITLE,
                      text: SHARE_TEXT,
                      url: APP_SMART_LINK_URL,
                    })
                    // A cancelled share rejects; that is not an error.
                    .catch(() => {});
                }}
                icon={
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#3a423d' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M12 16V3M8 7l4-4 4 4M20 14v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5' />
                  </svg>
                }
              />
            )}
          </div>

          <button
            type='button'
            onClick={() => setOpen(false)}
            className='mx-auto mt-4 block text-[13.5px] font-semibold text-[#8a8997]'
          >
            Not now
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
}

function LinkRow() {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(APP_SMART_LINK_URL);
    } catch (error) {
      console.error('Could not copy the Mia link:', error);
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <div className='mt-[18px] flex items-center gap-2.5 rounded-[13px] border border-[#ece9e1] bg-[#f4f3ef] px-3.5 py-3'>
      <svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke='#7a847d' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' className='shrink-0'>
        <path d='M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.8 1.7' />
        <path d='M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.8-1.7' />
      </svg>
      <span className='min-w-0 flex-1 truncate text-left text-[13.5px] text-[#3a423d]'>
        {APP_SMART_LINK_URL.replace(/^https?:\/\//, '')}
      </span>
      <button
        type='button'
        onClick={copy}
        className='shrink-0 text-[13px] font-bold text-[#256956]'
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

type ShareOptionProps = {
  label: string;
  icon: React.ReactNode;
} & (
  | { as: 'link'; href: string; onClick?: never }
  | { as: 'button'; onClick: () => void; href?: never }
);

function ShareOption({ label, icon, ...rest }: ShareOptionProps) {
  const className =
    'flex flex-1 flex-col items-center gap-1.5 rounded-[13px] border border-[#eceae3] bg-white py-[13px] text-[12px] font-semibold text-[#3a423d]';

  if (rest.as === 'link') {
    return (
      <a href={rest.href} target='_blank' rel='noreferrer' className={className}>
        {icon}
        {label}
      </a>
    );
  }

  return (
    <button type='button' onClick={rest.onClick} className={className}>
      {icon}
      {label}
    </button>
  );
}

export default ShareMia;
